import React, { useState } from "react";
import { AlertTriangle, ShieldAlert, Info, CheckCircle2, XCircle, ArrowRight } from "lucide-react";
import { StatusBadge } from "../StatusBadge";

const COVERAGE_STYLE = {
  UNDERSTAFFED: "border-red-200 bg-red-50 text-red-700",
  FULLY_STAFFED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  OVERSTAFFED: "border-blue-200 bg-blue-50 text-blue-700",
};
const COVERAGE_LABEL = { UNDERSTAFFED: "Understaffed", FULLY_STAFFED: "Fully staffed", OVERSTAFFED: "Overstaffed" };

function CoverageDay({ day, employeeName, navigate }) {
  if (!day.configured) {
    return (
      <div className="flex items-center justify-between text-xs bg-gray-50 border border-dashed border-gray-200 rounded-lg p-2.5" data-testid={`coverage-unconfigured-${day.date}`}>
        <span className="text-gray-500">{day.date}: No staffing plan configured for this period.</span>
        <button onClick={() => navigate("/management/scheduling/staffing-plan")} className="text-blue-600 font-semibold hover:underline shrink-0 ml-2" data-testid={`coverage-open-plan-${day.date}`}>Open Staffing Plan</button>
      </div>
    );
  }
  return (
    <div className="space-y-1.5" data-testid={`coverage-day-${day.date}`}>
      <p className="text-[11px] font-bold text-gray-500 uppercase">{day.date}</p>
      {day.blocks.length === 0 && <p className="text-xs text-gray-500">No time blocks defined for this day's plan.</p>}
      {day.blocks.map((b, i) => (
        <div key={i} className={`rounded-lg border p-2.5 ${COVERAGE_STYLE[b.status]}`} data-testid={`coverage-block-${day.date}-${i}`}>
          <div className="flex items-center justify-between text-xs font-semibold">
            <span>{b.start}-{b.end}{b.required_role ? ` (${b.required_role})` : ""}</span>
            <span>{COVERAGE_LABEL[b.status]}</span>
          </div>
          <p className="text-[11px] mt-0.5">
            Required: {b.required} - Assigned: {b.assigned}
            {b.difference < 0 && ` - ${-b.difference} employee${-b.difference > 1 ? "s" : ""} needed`}
            {b.difference > 0 && ` - ${b.difference} over preferred`}
          </p>
          {b.assigned_employee_ids.length > 0 && (
            <p className="text-[10px] opacity-70 truncate">{b.assigned_employee_ids.map(employeeName).join(", ")}</p>
          )}
        </div>
      ))}
    </div>
  );
}

/** Right pane of the 3-pane Schedule Builder - answers "what still needs management attention
 * before this schedule can be published?" via 4 sections fed from EXISTING sources of truth
 * (validate_schedule's own warnings, the new coverage endpoint that reads staffing_plans) -
 * never a second, independent calculation. */
export const SchedulingRightPane = ({ schedule, coverage, employees, permissions, actions, navigate }) => {
  const [coverageOpen, setCoverageOpen] = useState(true);
  const warnings = schedule.validation_warnings || [];
  const blocking = warnings.filter((w) => w.severity === "BLOCKING");
  const nonBlocking = warnings.filter((w) => w.severity === "WARNING");
  const info = warnings.filter((w) => w.severity === "INFO");
  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const isDraftEditable = ["DRAFT", "VALIDATION_REQUIRED", "BLOCKED", "READY_FOR_REVIEW"].includes(schedule.status);

  return (
    <div className="flex flex-col h-full overflow-y-auto" data-testid="schedule-right-pane">
      <div className="p-3 border-b border-gray-100">
        <button data-testid="coverage-section-toggle" onClick={() => setCoverageOpen((v) => !v)} className="text-xs font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1">
          Staffing Coverage
        </button>
        {coverageOpen && (
          <div className="mt-2 space-y-3" data-testid="staffing-coverage-list">
            {(coverage?.days || []).slice(0, 14).map((day) => (
              <CoverageDay key={day.date} day={day} employeeName={employeeName} navigate={navigate} />
            ))}
            {(!coverage || coverage.days.length === 0) && <p className="text-xs text-gray-500">Loading coverage...</p>}
          </div>
        )}
      </div>

      <div className="p-3 border-b border-gray-100 space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1"><ShieldAlert size={12} /> Blocking Issues ({blocking.length})</p>
        {blocking.length === 0 && <p className="text-xs text-gray-500">None - nothing is blocking submission/publish.</p>}
        {blocking.map((w, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs rounded-lg border border-red-200 bg-red-50 text-red-700 p-2" data-testid={`right-pane-blocking-${i}`}>
            <XCircle size={12} className="mt-0.5 shrink-0" />
            <span>{w.employee_id && `${employeeName(w.employee_id)} - `}{w.date && `${w.date} - `}{w.message}</span>
          </div>
        ))}
      </div>

      <div className="p-3 border-b border-gray-100 space-y-1.5">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500 flex items-center gap-1"><AlertTriangle size={12} /> Warnings ({nonBlocking.length})</p>
        {nonBlocking.length === 0 && <p className="text-xs text-gray-500">None.</p>}
        {nonBlocking.map((w, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs rounded-lg border border-amber-200 bg-amber-50 text-amber-700 p-2" data-testid={`right-pane-warning-${i}`}>
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            <span>{w.employee_id && `${employeeName(w.employee_id)} - `}{w.date && `${w.date} - `}{w.message}</span>
          </div>
        ))}
        {info.length > 0 && (
          <details className="text-xs text-gray-500 pt-1" data-testid="right-pane-info-details">
            <summary className="cursor-pointer">{info.length} informational note{info.length > 1 ? "s" : ""}</summary>
            {info.map((w, i) => (
              <p key={i} className="flex items-start gap-1.5 pt-1"><Info size={11} className="mt-0.5 shrink-0" /> {w.employee_id && `${employeeName(w.employee_id)} - `}{w.message}</p>
            ))}
          </details>
        )}
      </div>

      <div className="p-3 border-b border-gray-100 grid grid-cols-2 gap-y-1.5 gap-x-2 text-xs">
        <p className="text-gray-500">Status</p><p className="text-right"><StatusBadge status={schedule.status} /></p>
        <p className="text-gray-500">Version</p><p className="text-right font-semibold text-gray-700">{schedule.version}</p>
        <p className="text-gray-500">Created by</p><p className="text-right font-semibold text-gray-700 truncate">{schedule.created_by}</p>
        <p className="text-gray-500">Last saved</p><p className="text-right font-semibold text-gray-700">{(schedule.updated_at || "").slice(0, 16).replace("T", " ")}</p>
        <p className="text-gray-500">Approval</p><p className="text-right font-semibold text-gray-700">{schedule.approved_by ? `Approved by ${employeeName(schedule.approved_by)}` : "Not yet approved"}</p>
        <p className="text-gray-500">Publication</p><p className="text-right font-semibold text-gray-700">{schedule.published_at ? schedule.published_at.slice(0, 10) : "Not published"}</p>
      </div>

      <div className="p-3 space-y-2">
        <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Actions</p>
        <p className="text-[11px] text-gray-500 -mt-1">Assignments save immediately - there is no separate "save draft" step.</p>
        <div className="flex flex-col gap-2">
          {permissions.canEditSchedule && isDraftEditable && (
            <button data-testid="validate-schedule-button" onClick={actions.validate} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-gray-800 text-white text-sm font-semibold hover:bg-gray-900">
              <CheckCircle2 size={14} /> Validate
            </button>
          )}
          {permissions.canEditSchedule && schedule.status === "READY_FOR_REVIEW" && (
            <button data-testid="submit-for-review-button" onClick={actions.submitForReview} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700">
              <ArrowRight size={14} /> Submit for Approval
            </button>
          )}
          {permissions.canApproveSchedule && schedule.status === "UNDER_REVIEW" && (
            <button data-testid="approve-schedule-button" onClick={actions.approve} className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700">
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {permissions.canApproveSchedule && schedule.status === "APPROVED" && (
            <button
              data-testid="publish-schedule-button"
              disabled={blocking.length > 0}
              onClick={actions.publish}
              title={blocking.length > 0 ? "Resolve blocking issues before publishing" : undefined}
              className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-full bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Publish
            </button>
          )}
          {permissions.canEditSchedule && schedule.status === "PUBLISHED" && (
            <button data-testid="new-version-button" onClick={actions.newVersion} className="px-4 py-2 rounded-full bg-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-300">
              Create New Version
            </button>
          )}
          {schedule.status === "PUBLISHED" && <p className="text-[11px] text-gray-500">Published and immutable - changes require a new version.</p>}
        </div>
      </div>
    </div>
  );
};
